import type { MutableRefObject } from 'react';

import { type Option } from '../../../../types';
import { type OtherSettingProps } from '../../other-setting/types';
import { type V2Path } from '../../use-setting';

export type OptionComponentProps<OptionType = Option> = {
  option: OptionType;
  allOptions: OptionType[];
  index: number;
  onChange: (updateOption: OptionType) => any;
  onBlur: () => void;
  isCorrect?: boolean;
};

export type SetOptions<OptionType = Option> = (
  options: OptionType[],
  reorderInfo?: { currentIndex: number; nextIndex: number },
) => void;

export interface OptionListProps<OptionType = Option> {
  options: OptionType[];
  setOptions: SetOptions<OptionType>;
  optionCharacterLimit?: number;
  createNewOption: (options: OptionType[]) => OptionType;
  v2OptionsPath?: V2Path;
  v2CustomOptionPath?: V2Path;
  showCorrectAnswerSetting?: boolean;
  isSettingCorrectAnswerMode?: boolean;
  correctAnswerApplyRef?: MutableRefObject<(() => void) | null>;
}

export type HandleDefaultValueCallback<OptionType = Option> = ({
  option,
}: {
  option: OptionType;
}) => void;

export type OnOptionRemoveCallback<OptionType = Option> = ({
  option,
  options,
}: {
  option: OptionType;
  options: OptionType[];
}) => void;

export type OnDefaultValueCallback<OptionType = Option> = ({
  option,
}: {
  option: OptionType;
}) => void;

export type IsDefaultOptionCheck<OptionType = Option> = (
  option: OptionType,
) => boolean;

export type isLabelVisibleCheck<OptionType = Option> = ({
  option,
}: {
  option: OptionType;
}) => boolean;

export type OnLabelVisibilityCallback<OptionType = Option> = ({
  option,
}: {
  option: OptionType;
}) => void;

export type hasAltTextCheck<OptionType = Option> = ({
  option,
}: {
  option: OptionType;
}) => boolean;

export type isAltTextDisabledCheck<OptionType = Option> = ({
  option,
}: {
  option: OptionType;
}) => boolean;

export type OnAltTextCallback<OptionType = Option> = ({
  option,
}: {
  option: OptionType;
}) => void;

export type HandleOptionRemoveCallback<OptionType = Option> = ({
  option,
  index,
}: {
  option: OptionType;
  index: number;
}) => void;

export type RenderOptionFn<OptionType = Option> = (
  renderParams: OptionComponentProps<OptionType>,
) => React.ReactNode;

export type RenderOptionFnWithAltText<OptionType = Option> = (renderParams: {
  option: OptionType;
  options: OptionType[];
}) => React.ReactNode;

export enum SelectMode {
  oneByOne = 'one-by-one',
  onlyOne = 'only-one',
  many = 'many',
}

export interface OtherOptionProps extends OtherSettingProps {
  show?: boolean;
  toggleLabel?: string;
  addOtherInfoContent?: string;
  disabled?: boolean;
}
